/** Compiled wiki index: the template's fixed sections, then auto-generated topics, then saved questions; compile options and a context preview (child route of the app shell) */

import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { DEFAULT_CONTEXT_BUDGET, MAX_CONTEXT_BUDGET, wikiTemplateSections, type WikiPageSummary } from '@agentwiki/shared'
import { useProjectContext, useWikiPages } from '../hooks/use-project-content'
import { useProject } from '../hooks/use-projects'
import { projectPath } from '../components/shell/nav-config'
import { CompilePanel } from '../components/wiki/compile-panel'
import { groupWikiIndex } from '../lib/wiki-index'
import { useAppStore } from '../stores/app-store'
import { useT } from '../i18n'

function PageList({ project, pages }: { project: string; pages: WikiPageSummary[] }) {
  const t = useT()
  return (
    <ul className="divide-y divide-border rounded-xl border border-border">
      {pages.map((page) => (
        <li key={page.id}>
          <Link to={projectPath(project, `wiki/${page.slug}`)} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 hover:bg-surface-hover">
            <span className="min-w-0">
              <span className="block font-medium text-fg">{page.title}</span>
              {page.summary && <span className="block text-sm text-fg-muted">{page.summary}</span>}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-fg-subtle">
              {t('wiki.cited', { percent: Math.round(page.citationCoverage * 100) })} · {t('wiki.version', { version: page.version })}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function WikiIndexView({ project, pages, template }: { project: string; pages: WikiPageSummary[]; template: string | undefined }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const index = groupWikiIndex(pages, wikiTemplateSections(template), t('wiki.untopiced'))
  const hasSections = index.sections.length > 0

  return (
    <div className="space-y-6">
      {index.sections.map(({ section, pages: sectionPages }) => (
        <section key={section.slug} aria-labelledby={`wiki-section-${section.slug}`} className="space-y-2">
          <div>
            <h2 id={`wiki-section-${section.slug}`} className="text-sm font-semibold text-fg-subtle">{locale === 'vi' ? section.titleVi : section.title}</h2>
            <p className="text-xs text-fg-subtle">{locale === 'vi' ? section.purposeVi : section.purpose}</p>
          </div>
          {sectionPages.length ? (
            <PageList project={project} pages={sectionPages} />
          ) : (
            <p className="rounded-xl border border-dashed border-border px-4 py-3 text-sm text-fg-subtle">{t('wiki.noPageYet')}</p>
          )}
        </section>
      ))}
      {hasSections && index.topics.length > 0 && (
        <h2 className="border-t border-border pt-4 font-display text-base font-semibold text-fg">{t('wiki.topicsHeading')}</h2>
      )}
      {index.topics.map(([topic, topicPages]) => (
        <section key={topic} className="space-y-2">
          {hasSections ? <h3 className="text-sm font-semibold text-fg-subtle">{topic}</h3> : <h2 className="text-sm font-semibold text-fg-subtle">{topic}</h2>}
          <PageList project={project} pages={topicPages} />
        </section>
      ))}
      {index.questions.length > 0 && (
        <section aria-labelledby="wiki-saved-questions" className="space-y-2">
          <h2 id="wiki-saved-questions" className="border-t border-border pt-4 font-display text-base font-semibold text-fg">{t('wiki.savedQuestions')}</h2>
          <PageList project={project} pages={index.questions} />
        </section>
      )}
    </div>
  )
}

function ContextPreview({ project }: { project: string }) {
  const t = useT()
  const [task, setTask] = useState('')
  const [budget, setBudget] = useState(DEFAULT_CONTEXT_BUDGET)
  const [includeSources, setIncludeSources] = useState(true)
  const context = useProjectContext(project)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (task.trim()) context.mutate({ task: task.trim(), budget, includeSources })
  }

  return (
    <section className="space-y-3 rounded-xl border border-border bg-surface-raised p-4 md:p-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-fg">{t('context.title')}</h2>
        <p className="text-sm text-fg-muted">{t('context.hint')}</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-3 md:flex-row md:items-end">
        <label className="min-w-0 flex-1 space-y-1 text-sm text-fg-muted">
          <span>{t('context.task')}</span>
          <input
            value={task}
            onChange={(e) => setTask(e.target.value)}
            placeholder={t('context.taskPlaceholder')}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-fg placeholder:text-fg-subtle focus:border-accent"
          />
        </label>
        <label className="space-y-1 text-sm text-fg-muted md:w-36">
          <span>{t('context.budget')}</span>
          <input
            type="number"
            min={200}
            max={MAX_CONTEXT_BUDGET}
            step={100}
            value={budget}
            onChange={(e) => setBudget(Math.min(MAX_CONTEXT_BUDGET, Math.max(200, Number(e.target.value) || DEFAULT_CONTEXT_BUDGET)))}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 tabular-nums text-fg focus:border-accent"
          />
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-fg-muted">
          <input type="checkbox" checked={includeSources} onChange={(e) => setIncludeSources(e.target.checked)} />
          {t('context.includeSources')}
        </label>
        <button
          type="submit"
          disabled={context.isPending || !task.trim()}
          className="cursor-pointer rounded-lg bg-accent px-3.5 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-60"
        >
          {context.isPending ? t('context.running') : t('context.run')}
        </button>
      </form>
      {context.error && (
        <p role="alert" className="text-sm text-danger">{t('context.failed', { message: context.error.message })}</p>
      )}
      {context.data && (
        <div className="space-y-2">
          <p className="text-xs text-fg-subtle">
            {t('context.result', { items: context.data.items.length, used: context.data.usedTokens, budget: context.data.budget })}
          </p>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-surface-muted p-3 text-xs leading-relaxed text-fg">
            {context.data.markdown}
          </pre>
        </div>
      )}
    </section>
  )
}

export function ProjectWikiPage() {
  const t = useT()
  const { projectSlug = 'default' } = useParams<{ projectSlug: string }>()
  const { data, isLoading, error } = useWikiPages(projectSlug)
  const { data: project } = useProject(projectSlug)

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-6 md:px-8 md:py-10">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('wiki.title')}</h1>
        <p className="max-w-2xl text-sm text-fg-muted">{t('wiki.subtitle')}</p>
      </header>

      {isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : error ? (
        <p role="alert" className="text-sm text-danger">{t('wiki.loadError')}</p>
      ) : (
        <>
          {!data?.pages.length && (
            <div className="space-y-3 rounded-xl border border-dashed border-border p-6 text-sm text-fg-muted">
              <p>{t('wiki.empty')}</p>
              <pre className="overflow-x-auto rounded-lg bg-surface-muted p-3 text-xs text-fg">{`agentwiki compile --project ${projectSlug}\nagentwiki wiki put <slug> --title "<title>" --file page.md --project ${projectSlug}`}</pre>
            </div>
          )}
          <WikiIndexView project={projectSlug} pages={data?.pages ?? []} template={project?.wikiTemplate} />
        </>
      )}

      <CompilePanel project={projectSlug} />

      <ContextPreview project={projectSlug} />
    </div>
  )
}
